import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Avatar from '../../components/avatar/Avatar';
import InputField from '../FormControl/input/InputField';
import InputDateUTC from '../FormControl/input/InputDateUTC';
import SelectFieldYears from '../FormControl/SelectRequest/SelectFieldYears';
import IconTooltipButton from '../button/IconTooltipButton';
import CkeckboxRolesSkeleton from './CkeckboxRolesSkeleton';
import DialogConfirmCancel from '../dialog/DialogConfirm';
import TextEllipsis from '../text/TextEllipsis';
import { Checkbox, FormControlLabel, Paper } from '@mui/material';
import useFetchData from '../../hooks/useFetchData';
import SectionCollapse from '../section/SectionCollapse';
import FormatDate from '../date/FormatDate';
import { SelectFieldGender } from '../FormControl/SelectCustom/SelectFieldGender';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ContactInformation = ({
  form = {},
  isLoading,
  errors = {},
  onChange,
  isPreview = true,
  isLoadingForm
}) => (
  <SectionCollapse title='Contact information'>
    <Grid container spacing={2}>
      <Grid item md={2} sm={3}>
        <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
          Mobile
        </Typography>
      </Grid>
      <Grid item md={10} sm={9}>
        {isPreview ? (
          <Typography>{isLoading ? <Skeleton /> : form?.telephone}</Typography>
        ) : (
          <InputField
            id='telephone'
            name='telephone'
            value={form.telephone}
            error={errors.telephone}
            helperText={errors.telephone}
            disabled={isLoadingForm}
            onChange={(e) => onChange(e.target.name, e.target.value)}
          />
        )}
      </Grid>
      <Grid item md={2} sm={3}>
        <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
          Address
        </Typography>
      </Grid>
      <Grid item md={10} sm={9}>
        {isPreview ? (
          <Typography>{isLoading ? <Skeleton /> : form?.address}</Typography>
        ) : (
          <InputField
            id='address'
            name='address'
            value={form.address}
            error={errors.address}
            helperText={errors.address}
            disabled={isLoadingForm}
            onChange={(e) => onChange(e.target.name, e.target.value)}
          />
        )}
      </Grid>
      <Grid item md={2} sm={3}>
        <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
          Country
        </Typography>
      </Grid>
      <Grid item md={4} sm={3}>
        {isPreview ? (
          <Typography>{isLoading ? <Skeleton /> : form?.country}</Typography>
        ) : (
          <InputField
            id='country'
            name='country'
            value={form.country}
            error={errors.country}
            helperText={errors.country}
            disabled={isLoadingForm}
            onChange={(e) => onChange(e.target.name, e.target.value)}
          />
        )}
      </Grid>
      <Grid item md={2} sm={3}>
        <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
          State
        </Typography>
      </Grid>
      <Grid item md={4} sm={3}>
        {isPreview ? (
          <Typography>{isLoading ? <Skeleton /> : form?.state}</Typography>
        ) : (
          <InputField
            id='state'
            name='state'
            value={form.state}
            error={errors.state}
            helperText={errors.state}
            disabled={isLoadingForm}
            onChange={(e) => onChange(e.target.name, e.target.value)}
          />
        )}
      </Grid>
      <Grid item md={2} sm={3}>
        <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
          City
        </Typography>
      </Grid>
      <Grid item md={4} sm={3}>
        {isPreview ? (
          <Typography>{isLoading ? <Skeleton /> : form?.city}</Typography>
        ) : (
          <InputField
            id='city'
            name='city'
            value={form.city}
            error={errors.city}
            helperText={errors.city}
            disabled={isLoadingForm}
            onChange={(e) => onChange(e.target.name, e.target.value)}
          />
        )}
      </Grid>
      <Grid item md={2} sm={3}>
        <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
          Postal Code
        </Typography>
      </Grid>
      <Grid item md={4} sm={3}>
        {isPreview ? (
          <Typography>{isLoading ? <Skeleton /> : form?.postalCode}</Typography>
        ) : (
          <InputField
            id='postalCode'
            name='postalCode'
            value={form.postalCode}
            error={errors.postalCode}
            helperText={errors.postalCode}
            disabled={isLoadingForm}
            onChange={(e) => onChange(e.target.name, e.target.value)}
          />
        )}
      </Grid>
    </Grid>
  </SectionCollapse>
);

const StudentInformation = ({
  form = {},
  isLoading,
  errors = {},
  onChange,
  isPreview = true,
  isLoadingForm
}) => {
  const [modal, setModal] = useState(false);
  const [flagSelected, setFlagSelected] = useState([]);
  const flags = useSelector((state) => state?.tenant?.flags || []);

  // MF-502: ocultar los Flags (info sensible SEN/salud: IEP, Dyslexia, etc.)
  // cuando quien VE el perfil es un student o parent. Se evalua el rol del
  // usuario logueado (sesion), no el del perfil mostrado, para que teacher/admin
  // sigan viendo los flags al revisar el perfil de un alumno.
  // Staff-aware: solo se ocultan si el usuario es EXCLUSIVAMENTE student/parent
  // (every) — un staff con rol dual (p.ej. teacher que ademas es parent) los ve.
  const sessionRoles = useSelector((state) => state?.session?.user?.roles || [])
  const isStudentOrParent = (role) => {
    const id = role?.id?.toLowerCase()
    const name = role?.name?.toLowerCase()
    return id === 'student' || id === 'parent' || name === 'student' || name === 'parent'
  }
  const hideFlags = sessionRoles.length > 0 && sessionRoles.every(isStudentOrParent)

  useEffect(() => {
    setFlagSelected(form?.data?.flag || []);
  }, [form]);

  return (
    <>
      <SectionCollapse title='Student information'>
        <Grid container spacing={2} alignItems={'center'} justifyContent={'center'}>
          <Grid item md={2} sm={3}>
            <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
              Year level
            </Typography>
          </Grid>
          <Grid item md={4} sm={3}>
            {isPreview ? (
              <Typography>
                {isLoading ? <Skeleton /> : form?.yearLevel?.map((year) => year.name).join(', ')}
              </Typography>
            ) : (
              <SelectFieldYears
                id='year-level'
                name='yearLevelId'
                value={form.yearLevelId}
                error={errors.yearLevelId}
                helperText={errors.yearLevelId}
                disabled={isLoadingForm}
                onChange={(e) => onChange(e.target.name, e.target.value)}
              />
            )}
          </Grid>
          <Grid item md={2} sm={3}>
            <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
              Graduation year
            </Typography>
          </Grid>
          <Grid item md={4} sm={3}>
            {isPreview ? (
              <Typography>{isLoading ? <Skeleton /> : form?.data?.graduationYear}</Typography>
            ) : (
              <InputField
                id='graduationYear'
                name='graduationYear'
                value={form?.graduationYear}
                error={errors.graduationYear}
                helperText={errors.graduationYear}
                disabled={isLoadingForm}
                onChange={(e) => onChange(e.target.name, e.target.value)}
              />
            )}
          </Grid>
          <Grid item md={2} sm={3}>
            <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
              Form
            </Typography>
          </Grid>
          <Grid item md={4} sm={3}>
            {isPreview ? (
              <Typography>{isLoading ? <Skeleton /> : form?.data?.form}</Typography>
            ) : (
              <InputField
                id='form'
                name='form'
                value={form?.form}
                error={errors.form}
                helperText={errors.form}
                disabled={isLoadingForm}
                onChange={(e) => onChange(e.target.name, e.target.value)}
              />
            )}
          </Grid>
          <Grid item md={2} sm={3}>
            <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
              House
            </Typography>
          </Grid>
          <Grid item md={4} sm={3}>
            {isPreview ? (
              <Typography>{isLoading ? <Skeleton /> : form?.data?.house}</Typography>
            ) : (
              <InputField
                id='house'
                name='house'
                value={form?.house}
                error={errors.house}
                helperText={errors.house}
                disabled={isLoadingForm}
                onChange={(e) => onChange(e.target.name, e.target.value)}
              />
            )}
          </Grid>
          {!hideFlags && (
          <>
          <Grid item md={2} sm={3}>
            <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
              Flags
            </Typography>
          </Grid>
          <Grid item md={10} sm={9} className='flex items-center gap-4'>
            {form?.data?.flag?.map((flag, index) => {
              const flagInfo = flags?.find((f) => f.importName === flag) || null;

              return (
                <Chip
                  key={`item-flag-${index}-${flag}`}
                  label={flagInfo ? flagInfo?.name : flag}
                  icon={
                    flagInfo?.icon ? (
                      <div className='size-6 flex items-center justify-center rounded-full'>
                        <FontAwesomeIcon icon={flagInfo?.icon} size='xl' color={flagInfo?.color} />
                      </div>
                    ) : (
                      <div
                        className='size-6 flex items-center justify-center bg-muted text-muted-foreground rounded-full'
                        style={{ backgroundColor: flagInfo?.color }}
                      />
                    )
                  }
                  disabled={isLoading || isLoadingForm}
                />
              );
            })}
            {isPreview ? (
              <></>
            ) : (
              <IconTooltipButton
                title='Add/Remove flags'
                icon='pencil'
                onClick={() => setModal(true)}
                style={{
                  backgroundColor: 'var(--primary)',
                  color: 'var(--primary-foreground)',
                  padding: '0.45rem'
                }}
              />
            )}
          </Grid>
          </>
          )}
        </Grid>
      </SectionCollapse>{' '}
      <DialogConfirmCancel
        id='add-flag'
        open={modal}
        loading={isLoading || isLoadingForm}
        onCancel={() => setModal(false)}
        textCancel='Cancel'
        onConfirm={() => {
          onChange('flag', flagSelected);
          setModal(false);
        }}
        title='Add flag'
        maxWidth='xs'
        onCloseHeader={() => setConfirmDelete(false)}
        showActions={true}
      >
        {flags.length === 0 ? (
          <Alert severity='info'>No flags available. Please contact your administrator.</Alert>
        ) : (
          <Box className='flex flex-col gap-1'>
            {flags.map((flag, index) => (
              <div key={`flag-${index}-${flag._id}`} className='overflow-hidden'>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={`checkbox-flag-${index}-${flag._id}`}
                      checked={flagSelected?.includes(flag.importName) ? true : false}
                      name={flag._id}
                      onChange={(e) => {
                        let newFlags = new Set(flagSelected || []);
                        if (e.target.checked) {
                          newFlags.add(flag.importName);
                        } else {
                          newFlags.delete(flag.importName);
                        }
                        setFlagSelected([...newFlags]);
                      }}
                      color='primary'
                    />
                  }
                  label={
                    <div className='flex items-center gap-2'>
                      {flag?.icon ? (
                        <div className='size-6 flex items-center justify-center rounded-full'>
                          <FontAwesomeIcon icon={flag?.icon} size='xl' color={flag?.color} />
                        </div>
                      ) : (
                        <div
                          className='size-6 flex items-center justify-center bg-muted text-muted-foreground rounded-full'
                          style={{ backgroundColor: flag?.color }}
                        />
                      )}
                      <TextEllipsis id={`text-flag-${index}-${flag._id}`}>{flag.name}</TextEllipsis>
                    </div>
                  }
                />
              </div>
            ))}
          </Box>
        )}
      </DialogConfirmCancel>
    </>
  );
};

export const ChildrenInformation = ({ data = [], isLoading }) => (
  <SectionCollapse title={'Children'}>
    {isLoading ? (
      <Skeleton variant='rectangular' height={50} />
    ) : (
      <Grid container spacing={2}>
        {data.length > 0 ? (
          data.map((item, idx) => (
            <Grid item xs={6} key={`${idx}-${item._id}`} className='px-4'>
              <Paper className='flex items-center  gap-2 px-4 py-2'>
                <Avatar size='dashboard' variant='rounded' alt={item.code} src={`photos/${item.photo}`} />
                <div>
                  <Typography variant='subtitle1' color='primary'>
                    {item.code}
                  </Typography>
                  <Typography>{item.fullName}</Typography>
                  <Typography variant='inherit' className='text-xs'>
                    {item?.yearLevel?.map((year) => year.name).join(', ')}
                  </Typography>
                </div>
              </Paper>
            </Grid>
          ))
        ) : (
          <Grid item md={12} sm={12}>
            <Typography color='textSecondary' className='text-sm px-4'>
              You don't have any children yet
            </Typography>
          </Grid>
        )}
      </Grid>
    )}
  </SectionCollapse>
);

export const ParentInformation = ({ data = [], isLoading }) => (
  <SectionCollapse title='Parents'>
    {isLoading ? (
      <Skeleton variant='rectangular' height={50} />
    ) : (
      <Grid container spacing={2}>
        {data.length > 0 ? (
          data.map((item, idx) => (
            <Grid item xs={12} key={`${idx}-${item._id}`} className='px-4'>
              <Paper className='flex items-center  gap-2 px-4 py-2'>
                <Avatar size='dashboard' variant='rounded' alt={item.code} src={`photos/${item.photo}`} />
                <div>
                  <Typography variant='subtitle1' color='primary'>
                    {item.code}
                  </Typography>
                  <Typography>{item.fullName}</Typography>
                  <Typography variant='inherit' className='text-xs'>
                    {item?.email}
                  </Typography>
                </div>
              </Paper>
            </Grid>
          ))
        ) : (
          <Grid item md={12} sm={12}>
            <Typography color='textSecondary' className='text-sm px-4'>
              You don't have any parents yet
            </Typography>
          </Grid>
        )}
      </Grid>
    )}
  </SectionCollapse>
);

const DialogRoles = ({ show, onClose, onSave, formData = {} }) => {
  const [form, setForm] = useState(formData || {});
  const [radioRoles, setRadioRoles] = useState([]);
  const { isLoading, data: dataRoles, error } = useFetchData(`roles`);

  const onChange = (name, checked, isRadio) => {
    let rolesId = [];

    if (isRadio) {
      if (checked) {
        rolesId = [name];
      } else {
        rolesId = [];
      }
    } else {
      const role = form?.rolesId?.filter((item) => !radioRoles.includes(item));
      rolesId = new Set(role);

      if (checked) {
        rolesId.add(name);
      } else {
        rolesId.delete(name);
      }
      rolesId = [...rolesId];
    }
    const roles = dataRoles?.filter((role) => rolesId.includes(role.id)) || [];
    setForm({ ...form, rolesId: [...rolesId], roles });
  };

  useEffect(() => {
    const roles = dataRoles?.filter((role) => role.name === 'Student' || role.name === 'Parent') || [];
    setRadioRoles(roles.map((role) => role.id));
  }, [dataRoles]);

  const onApply = () => {
    onSave(form);
    onClose();
  };

  return (
    <DialogConfirmCancel
      id='user-roles'
      open={show}
      onCancel={onClose}
      onConfirm={onApply}
      textConfirm='Apply'
      title='Edit roles'
      onCloseHeader={true}
      maxWidth='sm'
    >
      <Typography variant='subtitle1' color='primary'>
        Roles
      </Typography>
      <Box className='col-span-3 grid grid-cols-3 gap-1'>
        {error ? (
          <Alert severity='error'>{error.message}</Alert>
        ) : isLoading ? (
          <CkeckboxRolesSkeleton />
        ) : (
          dataRoles
            ?.filter((role) => !radioRoles.includes(role.id))
            .map((role, index) => (
              <div key={`role-${index}-${role._id}`} className='overflow-hidden'>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={`checkbox-roles-${index}-${role._id}`}
                      checked={form?.rolesId?.includes(role.id) ? true : false}
                      name={role._id}
                      onChange={(e) => onChange(role.id, e.target.checked)}
                      color='primary'
                    />
                  }
                  label={<TextEllipsis id={`text-roles-${index}-${role._id}`}>{role.name}</TextEllipsis>}
                />
              </div>
            ))
        )}
      </Box>
    </DialogConfirmCancel>
  );
};

const UserFormSkeleton = ({
  form = {},
  isLoadingForm,
  isLoading,
  errors = {},
  onChange,
  isPreview = true,
  isPreviewLog,
  permission
}) => {
  const [show, setShow] = useState(false);

  return (
    <>
      {show && (
        <DialogRoles
          show={show}
          onClose={() => setShow(false)}
          onSave={(data) => onChange('rolesId', data)}
          formData={form}
        />
      )}
      <Box className='px-4 py-4'>
        <Grid container justifyContent='space-between'>
          <Grid item lg={2} sm={3} className={'flex justify-end'}>
            <Avatar
              size='large'
              variant='rounded'
              alt={form?.code}
              src={form?.photo ? `photos/${form?.photo}` : null}
            />
          </Grid>
          <Grid item lg={10} sm={9} container spacing={2}>
            <Grid item xs={6} className='grid grid-cols-3 items-center gap-4'>
              <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
                Roles
              </Typography>
              <div className='flex items-center justify-between col-span-2'>
                <div className='flex items-center flex-wrap gap-2'>
                  {form?.roles?.map((role) => (
                    <Chip
                      key={`item-role-${role._id}`}
                      label={role.name}
                      disabled={isLoading || isLoadingForm}
                    />
                  ))}
                  {errors.roles && <Typography color='error'>{errors.roles}</Typography>}
                </div>
                {form?.roles?.some((role) => !['Student', 'Parent'].includes(role.name)) &&
                  permission?.some((p) => p === 'roles') && (
                    <IconTooltipButton
                      disabled={isLoading || isLoadingForm}
                      title='Edit roles'
                      icon='cog'
                      onClick={() => setShow(true)}
                    />
                  )}
              </div>
            </Grid>
            <Grid item xs={6} className='grid grid-cols-3 items-center gap-4'>
              {form?.roles?.some((role) => role.name === 'Student') ? (
                <>
                  <Typography
                    variant='inherit'
                    color='primary'
                    className='font-bold'
                    align='right'
                    component='p'
                  >
                    Preferred Name
                  </Typography>
                  <div className='col-span-2'>
                    {isPreview ? (
                      <Typography>{isLoading ? <Skeleton /> : form?.preferredName}</Typography>
                    ) : (
                      <InputField
                        id='preferred-name'
                        name='preferredName'
                        value={form.preferredName}
                        error={errors.preferredName}
                        helperText={errors.preferredName}
                        disabled={isLoadingForm}
                        onChange={(e) => onChange(e.target.name, e.target.value)}
                      />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <Typography
                    variant='inherit'
                    color='primary'
                    className='font-bold'
                    align='right'
                    component='p'
                  >
                    Title
                  </Typography>
                  <div className='col-span-2'>
                    {isPreview ? (
                      <Typography>{isLoading ? <Skeleton /> : form?.title}</Typography>
                    ) : (
                      <InputField
                        id='title'
                        name='title'
                        value={form.title}
                        error={errors.title}
                        helperText={errors.title}
                        disabled={isLoadingForm}
                        onChange={(e) => onChange(e.target.name, e.target.value)}
                      />
                    )}
                  </div>
                </>
              )}
            </Grid>
            <Grid item xs={6} className='grid grid-cols-3 items-center gap-4'>
              <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
                First Name
              </Typography>
              <div className='col-span-2'>
                {isPreview ? (
                  <Typography>{isLoading ? <Skeleton /> : form?.firstName}</Typography>
                ) : (
                  <InputField
                    id='first-name'
                    name='firstName'
                    value={form.firstName}
                    error={errors.firstName}
                    helperText={errors.firstName}
                    disabled={isLoadingForm}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={6} className='grid grid-cols-3 items-center gap-4'>
              <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
                Last Name
              </Typography>
              <div className='col-span-2'>
                {isPreview ? (
                  <Typography>{isLoading ? <Skeleton /> : form?.lastName}</Typography>
                ) : (
                  <InputField
                    id='last-name'
                    name='lastName'
                    value={form.lastName}
                    error={errors.lastName}
                    helperText={errors.lastName}
                    disabled={isLoadingForm}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={6} className='grid grid-cols-3 items-center gap-4'>
              <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
                Code
              </Typography>
              <div className='col-span-2'>
                {isPreview ? (
                  <Typography className='overflow-hidden'>{isLoading ? <Skeleton /> : form?.code}</Typography>
                ) : (
                  <InputField
                    id='code'
                    name='code'
                    value={form.code}
                    error={errors.code}
                    helperText={errors.code}
                    disabled={isLoadingForm}
                    readOnly={permission?.some((p) => p === 'code') ? false : true}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={6} className='grid grid-cols-3 items-center gap-4'>
              <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
                Email
              </Typography>
              <div className='col-span-2'>
                {isPreview ? (
                  <Typography className='overflow-hidden'>
                    {isLoading ? <Skeleton /> : form?.email}
                  </Typography>
                ) : (
                  <InputField
                    id='email'
                    name='email'
                    value={form.email}
                    error={errors.email}
                    helperText={errors.email}
                    readOnly={permission?.some((p) => p === 'email') ? false : true}
                    disabled={isLoadingForm}
                    onChange={(e) => onChange(e.target.name, e.target.value)}
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={6} className='grid grid-cols-3 items-center gap-4'>
              <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
                Gender
              </Typography>
              <div className='col-span-2'>
                <SelectFieldGender
                  id='gender'
                  name='gender'
                  placeholder='Choose a gender'
                  value={form.gender}
                  error={errors.gender}
                  helperText={errors.gender}
                  disabled={isLoadingForm}
                  onChange={(e) => onChange(e.target.name, e.target.value)}
                  isPreview={isPreview}
                />
              </div>
            </Grid>
            <Grid item xs={6} className='grid grid-cols-3 items-center gap-4'>
              <Typography variant='inherit' color='primary' className='font-bold' align='right' component='p'>
                Date of birth
              </Typography>
              <div className='col-span-2'>
                {
                  isPreview ? (
                    <Typography>
                      {isLoading ? <Skeleton /> : form?.birthdate && <FormatDate date={form?.birthdate} />}
                    </Typography>
                  ) : (
                    <InputDateUTC
                      id='birthdate'
                      name='birthdate'
                      value={form.birthdate}
                      error={errors.birthdate}
                      helperText={errors.birthdate}
                      disabled={isLoadingForm}
                      onChange={(value) => onChange('birthdate', value)}
                    />
                  )
                  // : <></>
                }
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <ContactInformation
        form={form}
        isLoadingForm={isLoadingForm}
        isLoading={isLoading}
        isPreview={isPreview}
        errors={errors}
        onChange={onChange}
      />
      {form?.roles?.some((role) => role.name === 'Student') && (
        <StudentInformation
          form={form}
          isLoadingForm={isLoadingForm}
          isLoading={isLoading}
          isPreview={isPreview}
          errors={errors}
          onChange={onChange}
        />
      )}
      {form?.roles?.some((role) => role.name === 'Student') && (
        <ParentInformation data={form?.parents} isLoading={isLoading} />
      )}
      {form?.roles?.some((role) => role.name === 'Parent') && (
        <ChildrenInformation title='Children' data={form?.children} isLoading={isLoading} />
      )}
    </>
  );
};

export default UserFormSkeleton;
